import { useMemo } from "react";
import { Tooltip } from "antd";
import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import { useUser } from "@/contexts/UserContext";
import { useUserActions } from "@/hooks/useUserActions";
import type { ActionSourceFinding } from "@/utils/userActionsStore";

/**
 * "+" on a Trial News finding card: creates an action for that finding on the
 * Actions screen and then reads as added. It sits in the card's header, so
 * clicking it must not toggle the card open.
 */
export default function AddFindingActionButton({
  finding,
  roleLabel,
  trialId,
}: {
  finding: ActionSourceFinding;
  roleLabel?: string | null;
  trialId?: string | null;
}) {
  const { userInfo } = useUser();
  const { addFromFinding, addedFindingIds } = useUserActions();
  const added = addedFindingIds.has(finding.id);

  const assignedTo = useMemo(() => {
    const name = [userInfo?.first_name, userInfo?.last_name]
      .map((part) => part?.trim() ?? "")
      .filter(Boolean)
      .join(" ");
    return name || null;
  }, [userInfo?.first_name, userInfo?.last_name]);

  if (added) {
    return (
      <Tooltip title="Added to your actions">
        <span
          className="flex items-center gap-1 text-[12px] font-semibold text-[#004A42]"
          data-testid={`finding-added-${finding.id}`}
        >
          <CheckOutlined />
          Added
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Add to my actions">
      <button
        type="button"
        aria-label={`Add "${finding.title}" to my actions`}
        data-testid={`add-finding-action-${finding.id}`}
        className="flex h-6 w-6 items-center justify-center rounded-full border-0 bg-[#EFF1F2] p-0 leading-none text-[#06709B] hover:bg-[#D2EFF9]"
        onClick={(event) => {
          // The button lives inside the Collapse header, which toggles on click.
          event.stopPropagation();
          addFromFinding({ finding, roleLabel, trialId, assignedTo });
        }}
      >
        <PlusOutlined className="text-[12px]" />
      </button>
    </Tooltip>
  );
}
